/**
 * <shipping-discount-progress-bar>
 *
 * Visual progress bar toward the Freight Reward System's tiers, rendered by
 * snippets/freight-reward-bar.liquid in the cart drawer, on the cart page and (via the "Freight reward
 * bar" product block) on product pages. Nothing here repositions this element.
 *
 * Measures the cart by each line's original_line_price (before any discounts) — the checkout's freight
 * discount function runs alongside other discounts without seeing them, so product-level codes (e.g.
 * the 10% sign-up code) must not reduce the bar's progress either.
 *
 * Reads its tiers (a JSON array of { amount, message, badge }, already filtered to non-blank thresholds
 * and left for this element to sort) and total-price (cents) off its own attributes, and listens for the
 * theme's documented cart:change event to stay in sync without a re-fetch, the same way
 * <free-shipping-bar> (assets/theme.js) does.
 *
 * Tiers are spaced evenly along the bar by index, not by dollar amount — 1 tier sits at the end, 3
 * tiers sit at the thirds, etc. — with the fill interpolating smoothly within whichever segment the
 * current subtotal falls into. One circle is rendered per tier at its evenly-spaced position; a circle
 * fills with the bar color once its tier is reached and is otherwise the track color. The circles have
 * no transition/animation of their own — the color swap is instant — but each one is deliberately timed
 * to flip the moment the animating fill visually reaches it (see #animateCirclesIn, which polls the
 * fill's live computed transform every frame), not all at once when the whole animation ends and not
 * before the bar has actually gotten there. The highest tier is the "final" one: reaching it swaps in
 * the complete-message instead of a per-tier message.
 *
 * aria-valuenow always reflects the real subtotal immediately, on every render. Only the *visual* fill
 * (--progress) replays from empty on connect and on the cart drawer's dialog:after-show (which does
 * not bubble, so it's listened for on the closest <cart-drawer> ancestor directly, matching how
 * CartDrawer itself listens in theme.js) — never on assistive tech's reported value, which would
 * otherwise announce a misleading 0 mid-animation.
 */
class ShippingDiscountProgressBar extends HTMLElement {
  #currencyFormatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });
  #tiers = [];
  #circleElements = [];
  #completeMessage;
  #emptyMessage;
  #completeBadge;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #animationEnabled;
  #includeOrderDiscounts;
  #abortController;

  static get observedAttributes() {
    return ['total-price'];
  }

  constructor() {
    super();

    let rawTiers = [];
    try {
      rawTiers = JSON.parse(this.getAttribute('tiers') || '[]');
    } catch {
      rawTiers = [];
    }

    this.#tiers = rawTiers
      .map((tier) => ({
        amountCents: parseFloat(String(tier.amount || '0').replace(/[^0-9.]/g, '')) * 100,
        message: tier.message || '',
        badge: tier.badge || '',
      }))
      .filter((tier) => tier.amountCents > 0)
      .sort((a, b) => a.amountCents - b.amountCents);

    this.#completeMessage = this.getAttribute('complete-message') || '';
    this.#emptyMessage = this.getAttribute('empty-message') || '';
    this.#completeBadge = this.getAttribute('complete-badge') || '';
    this.#animationEnabled = !this.hasAttribute('no-animation');
    this.#includeOrderDiscounts = this.hasAttribute('include-order-discounts');
  }

  connectedCallback() {
    this.#buildCircles();

    this.#abortController = new AbortController();
    document.addEventListener('cart:change', this.#onCartChange, { signal: this.#abortController.signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal: this.#abortController.signal });

    this.closest('cart-drawer')?.addEventListener('dialog:after-show', this.#replay, {
      signal: this.#abortController.signal,
    });

    this.#render({ animate: true });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  attributeChangedCallback() {
    this.#render({ animate: false });
  }

  get totalPrice() {
    return parseFloat(this.getAttribute('total-price')) || 0;
  }

  set totalPrice(value) {
    this.setAttribute('total-price', value);
  }

  #buildCircles() {
    const track = this.querySelector('.shipping-discount-progress-bar__track');
    if (!track || this.#tiers.length === 0) return;

    // connectedCallback re-runs every time the cart drawer opens/closes (the theme's dialog moves itself
    // to <body> on open and back on close), so clear the previous set rather than stacking another.
    this.#circleElements.forEach((circle) => circle.remove());

    this.#circleElements =this.#tiers.map((tier, index) => {
      const circle = document.createElement('span');
      circle.className = 'shipping-discount-progress-bar__circle';
      circle.style.setProperty('--circle-position', `${((index + 1) / this.#tiers.length) * 100}%`);
      circle.style.setProperty('--circle-fraction', `${(index + 1) / this.#tiers.length}`);
      track.appendChild(circle);
      return circle;
    });
  }

  #onCartChange = (event) => {
    this.#applyCart(event.detail.cart);
  };

  // A page restored from the back/forward cache keeps whatever total it rendered with, even if the cart
  // changed on another page since (e.g. a product page the customer navigated back to) — re-read it.
  #onPageShow = async (event) => {
    if (!event.persisted) return;
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (response.ok) this.#applyCart(await response.json());
    } catch {
      // Keep showing the last known total rather than breaking the bar.
    }
  };

  #applyCart(cart) {
    const priceForItems = cart.items.reduce((sum, item) => sum + item.original_line_price, 0);
    const orderDiscount = this.#includeOrderDiscounts
      ? cart.cart_level_discount_applications.reduce((sum, discount) => sum + discount.total_allocated_amount, 0)
      : 0;
    this.totalPrice = priceForItems - orderDiscount;
  }

  #replay = () => {
    this.#render({ animate: true });
  };

  #render({ animate }) {
    if (this.#tiers.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const total = this.totalPrice;
    const messageElement = this.querySelector('.shipping-discount-progress-bar__message');
    const badgeElement = this.querySelector('.shipping-discount-progress-bar__badge');
    const tierCount = this.#tiers.length;
    const finalTier = this.#tiers[tierCount - 1];

    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', `${Math.round(finalTier.amountCents / 100)}`);
    this.setAttribute('aria-valuenow', `${Math.round(total / 100)}`);

    let message;
    let badge;
    let progress;

    if (total >= finalTier.amountCents) {
      message = this.#completeMessage;
      badge = this.#completeBadge;
      progress = 1;
    } else {
      const segmentIndex = this.#tiers.findIndex((tier) => total < tier.amountCents);
      const upperTier = this.#tiers[segmentIndex];
      const lowerAmount = segmentIndex === 0 ? 0 : this.#tiers[segmentIndex - 1].amountCents;
      const segmentSpan = upperTier.amountCents - lowerAmount;
      const segmentFraction = segmentSpan > 0 ? (total - lowerAmount) / segmentSpan : 0;
      progress = (segmentIndex + segmentFraction) / tierCount;
      message = upperTier.message.replace('{amount}', this.#currencyFormatter.format((upperTier.amountCents - total) / 100));
      badge = upperTier.badge;

      // Product placement only: a dedicated message while the cart is empty.
      if (total <= 0 && this.#emptyMessage) message = this.#emptyMessage;
    }

    if (messageElement) messageElement.textContent = message;
    if (badgeElement) {
      badgeElement.textContent = badge;
      badgeElement.hidden = !badge;
    }

    const applyCircles = () => {
      this.#circleElements.forEach((circle, index) => {
        circle.classList.toggle('is-achieved', total >= this.#tiers[index].amountCents);
      });
    };

    const applyFinalState = () => {
      this.style.setProperty('--progress', `${progress}`);
    };

    if (animate && this.#animationEnabled && !this.#reducedMotion && progress > 0) {
      const fill = this.querySelector('.shipping-discount-progress-bar__fill');

      // Covers the subtotal decreasing: any circle past where the bar is headed un-achieves right
      // away rather than waiting on the (forward-only) per-circle watcher below.
      this.#circleElements.forEach((circle, index) => {
        const fraction = (index + 1) / tierCount;
        if (fraction > progress + 0.0001) circle.classList.remove('is-achieved');
      });

      this.style.setProperty('--progress', '0');
      requestAnimationFrame(() => requestAnimationFrame(applyFinalState));
      this.#animateCirclesIn(fill, progress);
    } else {
      applyFinalState();
      applyCircles();
    }
  }

  /**
   * Watches the fill's live, currently-animating computed transform every frame and flips each circle
   * to achieved the instant the visual fill reaches its position — not before, and not held back until
   * the whole animation finishes, so a bar that crosses several tiers at once lights each circle up in
   * turn as it actually gets there.
   */
  #animateCirclesIn(fill, targetProgress) {
    if (!fill) return;

    const pending = this.#circleElements
      .map((circle, index) => ({ circle, fraction: (index + 1) / this.#tiers.length }))
      .filter(({ fraction }) => fraction <= targetProgress + 0.0001);

    if (pending.length === 0) return;

    const tick = () => {
      const current = this.#currentScaleX(fill);
      for (let i = pending.length - 1; i >= 0; i -= 1) {
        if (current >= pending[i].fraction - 0.001) {
          pending[i].circle.classList.add('is-achieved');
          pending.splice(i, 1);
        }
      }
      if (pending.length > 0) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  #currentScaleX(el) {
    const transform = getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    // DOMMatrixReadOnly rather than a regex: near the start of the ease-in the scale is reported in
    // exponent form (e.g. "matrix(1.2e-05, ...)"), which a digits-only pattern misreads as 1.2.
    return new DOMMatrixReadOnly(transform).a;
  }
}

if (!window.customElements.get('shipping-discount-progress-bar')) {
  window.customElements.define('shipping-discount-progress-bar', ShippingDiscountProgressBar);
}
